import { BlurEvent, Textbox, TableRow, TableRowMode, TableContentsChangedEvent, TableRowCreationEvent, Component, DataDisplayEvent, Label, Layout, ChangeEvent, TableRowDisplayEvent } from "@mcleod/components";
import { Currency, CurrencySettings, Api, CurrencyUtil, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutTableExtraPay } from "./autogen/AutogenLayoutTableExtraPay";
import { LMEUserSettings } from "@mcleod/general";

export class TableExtraPay extends AutogenLayoutTableExtraPay {

    private _payeeId: string
    private _currencySelected: string
    private _calculatePay: (event: TableContentsChangedEvent) => void;


    get payeeId(): string {
        return this._payeeId;
    }

    set payeeId(value: string) {
        this._payeeId = value;
    }

    get currencySelected(): string {
        return this._currencySelected;
    }

    set currencySelected(value: string) {
        this._currencySelected = value;
    }

    get calculatePay(): (event: TableContentsChangedEvent) => void {
        return this._calculatePay;
    }

    set calculatePay(value: (event: TableContentsChangedEvent) => void) {
        this._calculatePay = value;
    }

    textboxRateOnBlur(event: BlurEvent) {
        this.payAdjustmentsTextboxOnBlur(event);
    }


    textboxUnitsOnBlur(event: BlurEvent) {
        this.payAdjustmentsTextboxOnBlur(event);
    }

    tableExtraPayOnContentsChanged(event: TableContentsChangedEvent) {
        this.calculatePay(event);
    }

    tableExtraPayOnRowCreate(event: TableRowCreationEvent) {
        const modelRow = event.getTableRow().data as ModelRow;
        if (modelRow.get("payee_id") == null) {
            const payeeId = this.payeeId;
            modelRow.setValueAndLookupModelData("payee_id", payeeId, { id: payeeId });
        }
        if (modelRow._appending) {
            modelRow.set("order_id", this.activeRow?.get("orders.id"));
            modelRow.set("movement_id", this.activeRow?.get("id"));
            modelRow.set("entered_user_id", LMEUserSettings.getUserId());
            modelRow.set("transaction_date", new Date());
            const amount = modelRow.getCurrency("amount", null);
            if (amount) {
                amount.setConversionDate(this.activeRow?.get("orders.freight_charge_d", new Date()));
                modelRow.updateCurrency("amount", amount);
            }
        }
    }

    textboxDeductCodeIdOnChange(event: ChangeEvent) {
            const textbox = event.target as Textbox;
            if (event.userInitiatedChange && event.newValue != event.oldValue) {
                const tableRow = TableRow.getContainingTableRow(textbox);
                const modelRow = tableRow.data as ModelRow;
                const textboxShortDesc = tableRow.findComponentById("textboxShortDesc") as Textbox;
                const selectingFromDropdown = textbox.isDropdownVisible() && textbox.getFirstLookupModelData() != null;
                if (selectingFromDropdown) {
                    // after selecting a deduct code, set the textboxShortDesc.text and then textboxUnits gets focus
                    modelRow.set("short_desc", textbox.getFirstLookupModelData().get("short_desc"));
                    modelRow.set("code_type", textbox.getFirstLookupModelData().get("code_type"));
                    textbox.hideDropdown(false);
                    this.payAdjustmentsTextboxChanged(textbox);
                    tableRow.findComponentById("textboxUnits").focus();
                } else {
                    modelRow.set("short_desc", null);
                    textboxShortDesc.text = null;
                }
            }
    }

    payAdjustmentsChanged(): boolean {
            return this.tableExtraPay.dataSource.hasChanged();
    }

    payAdjustmentsTextboxOnBlur(event: BlurEvent) {
            if (event.changedWhileFocused) {
                const textbox: Textbox = event.target as Textbox;
                this.payAdjustmentsTextboxChanged(textbox);
            }
    }

    payAdjustmentsTextboxChanged(textbox: Textbox) {
        const tableRow: TableRow = TableRow.getContainingTableRow(textbox);
        const rate = tableRow.data.get("rate");
        const units = tableRow.data.get("units");
        tableRow.data.set("amount", new Currency({
            amount: rate * units,
            currency_code: this.currencySelected != null ? this.currencySelected : CurrencySettings.getFunctionalCurrency()
        }));
        if (textbox.text != null && textbox.text != "") {
            const payeeId = this.payeeId;
            Api.search("lme/dispatch/calculate-carrier-other-pay",
                {
                    "other_pay_row": TableRow.getContainingTableRow(textbox).data,
                    "payee_id": payeeId,
                    "currency_code": this.currencySelected != null ? this.currencySelected : CurrencySettings.getFunctionalCurrency()
                }).then(response => {
                this.payAdjustmentsFromApi(response, textbox);
            });
        } else {
            const descTextbox: Textbox = tableRow.findComponentById("textboxShortDesc") as Textbox;
            tableRow.data.set("short_desc", "");
            descTextbox.displayData(tableRow.data, null, 0);
        }
    }

    private payAdjustmentsFromApi(response, textbox: Textbox) {
        const data = response.data[0];
        const payAdjustments = data["other_pay_row"];
        const row = TableRow.getContainingTableRow(textbox);
        row.data.set("transaction_date", payAdjustments["transaction_date"]);

        const payeeIdTextbox: Textbox = row.findComponentById("textboxPayAdjustmentsPayeeId") as Textbox;
        const payeeId: string = payAdjustments["payee_id"];
        row.data.set("payee_id", payeeId);
        row.data.setLookupModelData("payee_id", payAdjustments["_lookup_payee_id"]);
        payeeIdTextbox.displayData(row.data, null, 0);

        const descTextbox: Textbox = row.findComponentById("textboxShortDesc") as Textbox;
        if (payAdjustments["short_desc"] != null)
            descTextbox.text = payAdjustments["short_desc"];

        const units: Textbox = row.findComponentById("textboxUnits") as Textbox;
        units.text = payAdjustments["units"]?.toString();
        const rate: Textbox = row.findComponentById("textboxRate") as Textbox;
        rate.text = payAdjustments["rate"]?.toString();
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            const currencyTo = this.currencySelected ?? CurrencySettings.getFunctionalCurrency();
            this.convertFlat(payAdjustments, currencyTo, ["amount"]);
        }
        const amountValue = payAdjustments["amount"] as Currency;
        TableRow.getContainingTableRow(textbox).data.set("amount", amountValue);

        const chargeRow: TableRow = TableRow.getContainingTableRow(textbox);

        chargeRow.data.set("amount", amountValue);
        const amount: Textbox = row.findComponentById("textboxAmount") as Textbox;
        if (row.mode === TableRowMode.QUICK_ADD)
            amount.text = CurrencyUtil.formatCurrency(amountValue)?.toString();
    }

    private convertFlat(data, currencyTo: string, fieldList: string[]) {
        fieldList.forEach(field => {
            const originalCurrency = (data instanceof ModelRow) ? data.get(field) as Currency : data[field] as Currency;
            if (originalCurrency) {
                const result = new Currency(originalCurrency.amount);
                result.conversion_date = originalCurrency.conversion_date;
                result.setCurrencyCode(currencyTo);
                if (data instanceof ModelRow) {
                    data.set(field, result);
                } else {
                    data[field] = result;
                }
            }
        });
    }

    textboxDeductCodeIdBeforeLookupModelSearch(event) {
        event.filter.dispatcher_flag = 'Y';
    }


    /** This is an event handler for the onRowDisplay event of tableExtraPay. */
    tableExtraPayOnRowDisplay(event: TableRowDisplayEvent) {
        const textboxAmount = event.getTableRow()?.findComponentById("textboxAmount") as Textbox;
        textboxAmount.quickInfoOverrideKeyValue = () => {
            const row = (event.target as TableRow)?.data as ModelRow;
            if (row?.get("fuel_surcharge_pay") != null) {
                return row.get("id");
            }
            return null;
        };

        const textboxDeductCodeId = event.getTableRow()?.findComponentById("textboxDeductCodeId") as Textbox;
        textboxDeductCodeId.quickInfoOverrideSearchMethod = async (layout: Layout, keyValue: any) => {
            if (!StringUtil.isEmptyString(keyValue)) {
                let row = TableRow.getContainingTableRow(event.target as Component)?.data;
                if (row == null) {
                    await layout.mainDataSource.search({ search: keyValue }).then((response) => {
                        row = response.modelRows?.[0];
                    });
                }
                layout["textboxDriveExtraPayId"].text = keyValue; //deduct_code_id
                layout["textboxDeductDescr"].text = row?.get("short_desc");
            }
        };
    }
}
